/**
 * Pure state helpers for the text section's inline Edit → Save
 * (ContentEditor.saveSection ↔ PUT /studio/framework/{draft}/section/{id}).
 *
 * Besides `content`, the editor changes three "extras": subsection
 * titles/prose, objectives and takeaways. Only the extras that actually
 * changed are sent, each with the value editing started from so the server
 * can refuse (409) when someone else changed it meanwhile.
 */

export const EXTRA_FIELDS = ['subsections', 'objectives', 'takeaways'];

/** What the editor shows for a section — mirrors backend editable_projection. */
export function editableExtras(section) {
  return {
    subsections: (section?.subsections || [])
      .filter((s) => s && typeof s === 'object')
      .map((s) => ({ title: s.title || '', content: s.content || '' })),
    objectives: [...(section?.objectives || [])],
    takeaways: [...(section?.takeaways || [])],
  };
}

// List editors keep blank lines while typing; they're not real items.
const withoutBlankLines = (items) => items.filter((line) => String(line).trim());

function comparable(extras, field) {
  const value = extras[field];
  return JSON.stringify(field === 'subsections' ? value : withoutBlankLines(value));
}

export function changedExtraFields(startExtras, extras) {
  return EXTRA_FIELDS.filter((f) => comparable(startExtras, f) !== comparable(extras, f));
}

export function isEditDirty(start, content, extras) {
  if (!start) return false;
  return content !== start.displayed || changedExtraFields(start.extras, extras).length > 0;
}

/** True when the stored section moved on since editing started (AI edit, undo). */
export function changedSinceEditStart(start, storedSection, fields) {
  if ((storedSection?.content || '') !== start.stored) return true;
  const now = editableExtras(storedSection);
  return fields.some((f) => JSON.stringify(now[f]) !== JSON.stringify(start.extras[f]));
}

/** PUT body; `force` drops the expected_* guards (teacher chose to overwrite). */
export function buildSectionSaveBody(start, content, extras, { force = false } = {}) {
  const fields = changedExtraFields(start.extras, extras);
  const body = { content };
  fields.forEach((f) => { body[f] = f === 'subsections' ? extras[f] : withoutBlankLines(extras[f]); });
  if (!force) {
    body.expected_content = start.stored;
    if (fields.length) body.expected_fields = Object.fromEntries(fields.map((f) => [f, start.extras[f]]));
  }
  return body;
}

/** The section as stored after a successful save of `body`. */
export function applySavedSection(section, body, res) {
  const next = { ...section, content: body.content, highlights: res?.highlights ?? section.highlights };
  if (body.objectives) next.objectives = body.objectives;
  if (body.takeaways) next.takeaways = body.takeaways;
  if (body.subsections) {
    next.subsections = (section.subsections || []).map((sub, i) => ({
      ...sub,
      ...body.subsections[i],
      highlights: res?.subsection_highlights?.[i] ?? sub.highlights,
    }));
  }
  return next;
}
